import type {IconButtonProps} from "@mui/material/IconButton";
import {useState, useCallback} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuItem, {menuItemClasses} from "@mui/material/MenuItem";
import {useRouter, usePathname} from "routes/hooks";
import {_myAccount} from "_mock";
import useAuthStore from "store/useAuthStore";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import useGetHeader from "../../sections/auth/hooks/useGetHeader";
import useLogin from "../../sections/auth/hooks/useLogin";
import useLogout from "../../sections/auth/hooks/useLogout";

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
    data?: {
        label: string;
        href: string;
        icon?: React.ReactNode;
        info?: React.ReactNode;
    }[];
};

export function AccountPopover({
                                   data = [],
                                   sx,
                                   ...other
                               }: AccountPopoverProps) {
    const router = useRouter();

    const {logout, user} = useAuthStore();

    const pathname = usePathname();

    const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(
        null
    );

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const handleOpenPopover = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setOpenPopover(event.currentTarget);
        },
        []
    );

    const handleClosePopover = useCallback(() => {
        setOpenPopover(null);
    }, []);

    const handleClickItem = useCallback(
        (path: string) => {
            handleClosePopover();
            router.push(path);
        },
        [handleClosePopover, router]
    );

    const {mutateAsync: submitCsrfToken, isPending} = useGetHeader();
    const {mutateAsync: submitLogout, isPending: isLoading} = useLogout();

    const confirmLogout = useCallback(() => {
        setShowLogoutConfirm(false);
        handleClosePopover();
        submitCsrfToken()
            .then((response) => {
                submitLogout()
                    .then((response) => {
                        logout();
                        router.push("/");
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            })
            .catch((error) => console.log("error", error));
    }, [setShowLogoutConfirm, handleClosePopover, logout]);

    return (
        <>
            <IconButton
                onClick={handleOpenPopover}
                sx={{
                    p: "2px",
                    width: 40,
                    height: 40,
                    background: (theme) =>
                        `conic-gradient(${theme.vars.palette.primary.light}, ${theme.vars.palette.warning.light}, ${theme.vars.palette.primary.light})`,
                    ...sx,
                }}
                {...other}
            >
                <Avatar
                    src={user?.photoURL}
                    alt={user?.first_name}
                    sx={{width: 1, height: 1}}
                >
                    {user?.first_name?.charAt(0)?.toUpperCase()}
                </Avatar>
            </IconButton>

            <Popover
                open={!!openPopover}
                anchorEl={openPopover}
                onClose={handleClosePopover}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{
                    paper: {
                        sx: {width: 200},
                    },
                }}
            >
                <Box sx={{p: 2, pb: 1.5}}>
                    <Typography variant="subtitle2" noWrap>
                        {user?.first_name} {user?.last_name}
                        {/* {user?.first_name + user?.last_name} */}
                    </Typography>

                    <Typography variant="body2" sx={{color: "text.secondary"}} noWrap>
                        {user?.email}
                    </Typography>
                </Box>

                <Divider sx={{borderStyle: "dashed"}}/>

                <MenuList
                    disablePadding
                    sx={{
                        p: 1,
                        gap: 0.5,
                        display: "flex",
                        flexDirection: "column",
                        [`& .${menuItemClasses.root}`]: {
                            px: 1,
                            gap: 2,
                            borderRadius: 0.75,
                            color: "text.secondary",
                            "&:hover": {color: "text.primary"},
                            [`&.${menuItemClasses.selected}`]: {
                                color: "text.primary",
                                bgcolor: "action.selected",
                                fontWeight: "fontWeightSemiBold",
                            },
                        },
                    }}
                >
                    {data.map((option) => (
                        <MenuItem
                            key={option.label}
                            selected={option.href === pathname}
                            onClick={() => handleClickItem(option.href)}
                        >
                            {option.icon}
                            {option.label}
                        </MenuItem>
                    ))}
                </MenuList>

                <Divider sx={{borderStyle: "dashed"}}/>

                <Box sx={{p: 1}}>
                    <Button
                        fullWidth
                        color="error"
                        size="medium"
                        variant="text"
                        onClick={() => setShowLogoutConfirm(true)}
                    >
                        خروج
                    </Button>
                </Box>
            </Popover>

            <Dialog
                open={showLogoutConfirm}
                onClose={() => setShowLogoutConfirm(false)}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
                sx={{"& .MuiDialog-paper": {maxWidth: 435, padding: 1}}}
            >
                <DialogTitle id="logout-dialog-title">خروج از حساب کاربری</DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description">
                        آیا مطمئن هستید که می‌خواهید از حساب کاربری خود خارج شوید؟
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowLogoutConfirm(false)}>انصراف</Button>
                    <Button color="error" onClick={confirmLogout} autoFocus>
                        خروج
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
